import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui';
import { SERVICES, PERMISSION_ACTIONS, SERVICE_DESCRIPTIONS, ACTION_DESCRIPTIONS } from '../../shared/config/permissions';

interface PermissionSelectorSimpleProps {
  value: string[];
  onChange: (permissions: string[]) => void;
  disabled?: boolean;
}

export const PermissionSelectorSimple: React.FC<PermissionSelectorSimpleProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = React.useState<string>('');
  const [selectedActions, setSelectedActions] = React.useState<string[]>([]);

  // Обновляем разрешения при изменении сервиса или действий
  React.useEffect(() => {
    if (selectedService && selectedActions.length > 0) {
      const newPermissions = selectedActions.map(action => `${selectedService}:${action}`);
      onChange([...value.filter(p => !p.startsWith(`${selectedService}:`)), ...newPermissions]);
    }
  }, [selectedService, selectedActions, onChange]);

  const handleServiceChange = (service: string) => {
    setSelectedService(service);
    setSelectedActions([]);
  };

  const handleActionToggle = (action: string) => {
    setSelectedActions(prev => 
      prev.includes(action) 
        ? prev.filter(a => a !== action)
        : [...prev, action]
    );
  };

  const addGlobalPermission = () => {
    if (!value.includes('*')) {
      onChange([...value, '*']);
    }
  };

  const removePermission = (permission: string) => {
    onChange(value.filter(p => p !== permission));
  };

  return (
    <div className="space-y-4">
      {/* Глобальные разрешения */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('roles.globalPermissions', 'Глобальные разрешения')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="global-all"
              checked={value.includes('*')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addGlobalPermission();
                } else {
                  removePermission('*');
                }
              }}
              disabled={disabled}
            />
            <label htmlFor="global-all" className="text-sm">
              Все разрешения (суперпользователь)
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Сервисные разрешения */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('roles.servicePermissions', 'Разрешения сервисов')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Выбор сервиса */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('roles.selectService', 'Выберите сервис')}
            </label>
            <Select value={selectedService} onValueChange={handleServiceChange} disabled={disabled}>
              <SelectTrigger>
                <SelectValue placeholder={t('roles.selectService', 'Выберите сервис')} />
              </SelectTrigger>
              <SelectContent>
                {Object.values(SERVICES).map(service => (
                  <SelectItem key={service} value={service}>
                    {SERVICE_DESCRIPTIONS[service as keyof typeof SERVICE_DESCRIPTIONS] || service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Выбор действий */}
          {selectedService && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('roles.actions', 'Действия')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[PERMISSION_ACTIONS.READ, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE].map(action => (
                  <div key={action} className="flex items-center space-x-2">
                    <Checkbox
                      id={`action-${action}`}
                      checked={selectedActions.includes(action)}
                      onCheckedChange={() => handleActionToggle(action)}
                      disabled={disabled}
                    />
                    <label htmlFor={`action-${action}`} className="text-sm">
                      {ACTION_DESCRIPTIONS[action as keyof typeof ACTION_DESCRIPTIONS] || action}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Текущие разрешения */}
      {value.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('roles.permissionsPreview', 'Текущие разрешения')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {value.map(permission => (
                <Badge 
                  key={permission} 
                  variant="secondary" 
                  className="text-xs flex items-center gap-1"
                >
                  {permission}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => removePermission(permission)}
                    disabled={disabled}
                  >
                    ×
                  </Button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};